// Expense approvals (manager + finance) and expense setup (categories,
// policy). All actions hit the real APIs; decisions carry the report version
// for optimistic-concurrency protection against double approval.
import { formatDate } from '@/lib/orgFormat';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, Inbox, Settings2, Banknote } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import ExpenseAccountingCard from '@/features/expenses/ExpenseAccountingCard';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import { useHasPermission, useHasAnyPermission } from '@/rbac/useRbac';
import {
  listApprovals, approveReport, rejectReport, requestChanges, reimburseReport,
  listCategories, upsertCategory, getPolicy, savePolicy, publishPolicy,
  reviewReceipt, settleReimbursement, advanceAction, exportRegister,
  type ExpenseReport, type ExpenseCategory, type ReportDetail, type ExpenseAdvance,
} from '@/lib/expensesApi';
import { ReportDetailView, StatusChip, AdvancesPanel } from '../employee/ExpensesPage';
import { ImprestRetirementQueue, ReimbursementSLAPanel } from '@/features/expenses/ImprestPanels';
import { ImprestPolicyPanel } from '@/features/expenses/ImprestPolicyPanel';
import { usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';

const input = 'w-full h-10 px-3 rounded-lg border border-border bg-background text-foreground text-sm focus:border-orange-400 focus:outline-none';

const DecisionActions: React.FC<{ detail: ReportDetail; refresh: () => void }> = ({ detail, refresh }) => {
  const { t } = useTranslation(['expenses']);
  const canFinance = useHasPermission('expenses:finance');
  const canReimburse = useHasPermission('expenses:reimburse');
  // Phase 4 (authz-hardening): decisions need a reviewer grant, not just a
  // reviewable status — backend enforces; this keeps the UI truthful.
  const canDecide = useHasAnyPermission(['expenses:review', 'expenses:approve', 'expenses:finance']);
  const [busy, setBusy] = useState(false);
  const promptText = usePrompt();
  const { report } = detail;
  const reviewable = canDecide && ['SUBMITTED', 'MANAGER_REVIEW', 'FINANCE_REVIEW'].includes(report.status);

  const act = async (fn: () => Promise<unknown>, okMsg: string) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      refresh();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('expenses:errors.decision_failed', { defaultValue: 'Could not record the decision.' })));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex gap-2 flex-wrap">
      {reviewable && (
        <>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy}
            onClick={() => act(() => approveReport(report.id, report.version),
              t('expenses:toasts.approved', { defaultValue: 'Approved.' }))}>
            {t('expenses:buttons.approve', { defaultValue: 'Approve' })}
          </Button>
          <Button size="sm" variant="outline" disabled={busy}
            onClick={async () => {
              const reason = await promptText({ title: t('expenses:buttons.request_changes', { defaultValue: 'Request changes' }), label: t('expenses:prompts.changes_reason', { defaultValue: 'What should the employee change?' }), required: true, multiline: true, confirmLabel: t('expenses:buttons.request_changes', { defaultValue: 'Request changes' }) });
              if (reason?.trim()) void act(() => requestChanges(report.id, reason.trim(), report.version),
                t('expenses:toasts.changes_requested', { defaultValue: 'Changes requested.' }));
            }}>
            {t('expenses:buttons.request_changes', { defaultValue: 'Request changes' })}
          </Button>
          <Button size="sm" variant="outline" className="text-red-600 border-red-300" disabled={busy}
            onClick={async () => {
              const reason = await promptText({ title: t('expenses:buttons.reject', { defaultValue: 'Reject' }), label: t('expenses:prompts.reject_reason', { defaultValue: 'Reason for rejection (required):' }), required: true, multiline: true, danger: true, confirmLabel: t('expenses:buttons.reject', { defaultValue: 'Reject' }) });
              if (reason?.trim()) void act(() => rejectReport(report.id, reason.trim(), report.version),
                t('expenses:toasts.rejected', { defaultValue: 'Rejected.' }));
            }}>
            {t('expenses:buttons.reject', { defaultValue: 'Reject' })}
          </Button>
        </>
      )}
      {canFinance && detail.receipts.length > 0 && ['MANAGER_REVIEW', 'FINANCE_REVIEW', 'SUBMITTED'].includes(report.status) && (
        <>
          {detail.receipts.map((rc) => (
            <span key={rc.id} className="inline-flex items-center gap-1">
              <Button size="sm" variant="outline" disabled={busy}
                onClick={() => act(() => reviewReceipt(rc.id, 'accepted', true),
                  t('expenses:toasts.receipt_accepted', { defaultValue: 'Receipt accepted & verified.' }))}>
                {t('expenses:buttons.accept_receipt', { defaultValue: 'Accept receipt' })}
              </Button>
              <Button size="sm" variant="outline" disabled={busy}
                onClick={() => act(() => reviewReceipt(rc.id, 'returned'),
                  t('expenses:toasts.receipt_returned', { defaultValue: 'Receipt returned.' }))}>
                {t('expenses:buttons.return_receipt', { defaultValue: 'Return' })}
              </Button>
            </span>
          ))}
        </>
      )}
      {report.status === 'SCHEDULED' && canReimburse && detail.reimbursement && (
        <>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy}
            onClick={async () => {
              const entered = await promptText({ title: t('expenses:buttons.confirm_paid', { defaultValue: 'Confirm paid' }), label: t('expenses:prompts.payment_ref', { defaultValue: 'Payment reference (bank/petty-cash ref):' }), confirmLabel: t('expenses:buttons.confirm_paid', { defaultValue: 'Confirm paid' }) });
              if (entered == null) return;
              const ref = entered;
              void act(() => settleReimbursement(detail.reimbursement!.id, 'paid', ref.trim()),
                t('expenses:toasts.settled', { defaultValue: 'Reimbursement confirmed paid.' }));
            }}>
            {t('expenses:buttons.confirm_paid', { defaultValue: 'Confirm paid' })}
          </Button>
          <Button size="sm" variant="outline" className="text-red-600 border-red-300" disabled={busy}
            onClick={async () => {
              const why = await promptText({ title: t('expenses:buttons.mark_failed', { defaultValue: 'Payment failed' }), label: t('expenses:prompts.failure_reason', { defaultValue: 'Failure reason:' }), required: true, multiline: true, danger: true, confirmLabel: t('expenses:buttons.mark_failed', { defaultValue: 'Payment failed' }) });
              if (why?.trim()) void act(() => settleReimbursement(detail.reimbursement!.id, 'failed', '', why.trim()),
                t('expenses:toasts.marked_failed', { defaultValue: 'Marked as payment failed.' }));
            }}>
            {t('expenses:buttons.mark_failed', { defaultValue: 'Payment failed' })}
          </Button>
        </>
      )}
      {report.status === 'APPROVED' && canFinance && canReimburse && (
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy}
          onClick={async () => {
            const entered = await promptText({ title: t('expenses:buttons.reimburse', { defaultValue: 'Reimburse' }), label: t('expenses:prompts.payment_ref', { defaultValue: 'Payment reference (bank/petty-cash ref):' }), confirmLabel: t('expenses:buttons.reimburse', { defaultValue: 'Reimburse' }) });
            if (entered == null) return;
            const ref = entered;
            void act(() => reimburseReport(report.id, 'manual', ref.trim(), report.version),
              t('expenses:toasts.reimbursed', { defaultValue: 'Marked reimbursed.' }));
          }}>
          <Banknote className="w-4 h-4 mr-1" />{t('expenses:buttons.reimburse', { defaultValue: 'Reimburse' })}
        </Button>
      )}
    </div>
  );
};

const SetupTab: React.FC = () => {
  const { t } = useTranslation(['expenses']);
  // HR configures the imprest cycle; Finance owns the spend limits above it.
  // Separate permissions, so the two halves of this tab appear independently.
  const canImprest = useHasPermission('expenses:imprest:configure');
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [rules, setRules] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listCategories().then(setCategories).catch(() => setCategories([]));
    getPolicy().then((p) => setRules(p.rules || {})).catch(() => undefined);
  }, []);
  useEffect(load, [load]);

  const ruleStr = (k: string) => String((rules as any)[k] ?? '');
  const setRule = (k: string, v: string) => setRules((r) => ({ ...r, [k]: v }));

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader><CardTitle className="text-base">{t('expenses:setup.categories', { defaultValue: 'Categories' })}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <ul className="divide-y divide-border">
            {categories.map((c) => (
              <li key={c.id} className="py-2 flex items-center gap-2 text-sm">
                <span className="font-medium text-foreground">{c.name}</span>
                <span className="text-xs text-muted-foreground">{c.code}</span>
                {!c.active && <span className="text-xs text-muted-foreground">({t('expenses:setup.inactive', { defaultValue: 'inactive' })})</span>}
              </li>
            ))}
            {categories.length === 0 && (
              <li className="py-3 text-sm text-muted-foreground">{t('expenses:setup.no_categories', { defaultValue: 'No categories yet — add the first one.' })}</li>
            )}
          </ul>
          <div className="flex gap-2 flex-wrap">
            <input className={`${input} w-32`} placeholder="code" value={newCode} onChange={(e) => setNewCode(e.target.value)} />
            <input className={`${input} flex-1 min-w-[140px]`} placeholder={t('expenses:setup.name', { defaultValue: 'Name' })} value={newName} onChange={(e) => setNewName(e.target.value)} />
            <Button size="sm" variant="outline" disabled={!newCode.trim() || !newName.trim()}
              onClick={() => upsertCategory({ code: newCode.trim(), name: newName.trim() })
                .then(() => { setNewCode(''); setNewName(''); load(); toast.success(t('expenses:toasts.category_saved', { defaultValue: 'Category saved.' })); })
                .catch((e) => toast.error(getApiErrorMessage(e, 'Could not save the category.')))}>
              {t('expenses:buttons.add_category', { defaultValue: 'Add' })}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">{t('expenses:setup.policy', { defaultValue: 'Expense policy' })}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {[
            ['max_item_amount', t('expenses:setup.max_item', { defaultValue: 'Max per item (base currency)' })],
            ['max_report_amount', t('expenses:setup.max_report', { defaultValue: 'Max per report' })],
            ['receipt_required_over', t('expenses:setup.receipt_over', { defaultValue: 'Receipt required over' })],
            ['max_age_days', t('expenses:setup.max_age', { defaultValue: 'Submission age limit (days)' })],
          ].map(([k, lbl]) => (
            <div key={k}>
              <span className="block text-sm font-medium text-foreground mb-1">{lbl}</span>
              <input className={input} inputMode="decimal" value={ruleStr(k)} onChange={(e) => setRule(k, e.target.value)} />
            </div>
          ))}
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white" disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const clean: Record<string, unknown> = {};
                for (const [k, v] of Object.entries(rules)) {
                  if (v !== '' && v != null) clean[k] = k === 'max_age_days' ? Number(v) : v;
                }
                const { version } = await savePolicy(clean);
                await publishPolicy(version);
                toast.success(t('expenses:toasts.policy_published', { defaultValue: 'Policy v{{v}} published.', v: version }));
              } catch (e) {
                toast.error(getApiErrorMessage(e, 'Could not publish the policy.'));
              } finally {
                setBusy(false);
              }
            }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {t('expenses:buttons.publish_policy', { defaultValue: 'Save & publish' })}
          </Button>
        </CardContent>
      </Card>

      {canImprest && <ImprestPolicyPanel />}
    </div>
  );
};

export const ExpenseApprovalsPage: React.FC = () => {
  const { t } = useTranslation(['expenses']);
  const canPolicy = useHasPermission('expenses:policy:manage');
  const canExport = useHasPermission('expenses:export');
  const [tab, setTab] = useState<'queue' | 'advances' | 'setup'>('queue');
  const [reports, setReports] = useState<ExpenseReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    listApprovals()
      .then(setReports)
      .catch((e) => toast.error(getApiErrorMessage(e, t('expenses:errors.load_failed', { defaultValue: 'Could not load approvals.' }))))
      .finally(() => setLoading(false));
  }, [t]);
  useEffect(load, [load]);

  if (selected) {
    return (
      <ReportDetailView
        reportId={selected}
        readOnly
        onBack={() => { setSelected(null); load(); }}
        actions={(detail, refresh) => (
          <div className="space-y-4">
            <DecisionActions detail={detail} refresh={refresh} />
            {/* Recognising the expense in the ledger is a separate act from
                paying it, and can happen before OR after the cash moves — so
                the card decides for itself from the status rather than hiding
                behind the reimbursement actions. */}
            <ExpenseAccountingCard reportId={detail.report.id} reportStatus={detail.report.status} />
          </div>
        )}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('expenses:approvals.title', { defaultValue: 'Expense approvals' })}</h1>
          <p className="text-muted-foreground text-sm">{t('expenses:approvals.description', { defaultValue: 'Review, approve and reimburse expense reports.' })}</p>
        </div>
        {canExport && (
          <Button variant="outline" size="sm" onClick={() => exportRegister().catch((e) => toast.error(getApiErrorMessage(e, 'Export failed.')))}>
            {t('expenses:buttons.export_register', { defaultValue: 'Export register (CSV)' })}
          </Button>
        )}
      </div>

      {canPolicy && (
        <div className="flex gap-1 rounded-full bg-muted p-1 w-fit">
          <button type="button" onClick={() => setTab('queue')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${tab === 'queue' ? 'bg-background text-foreground shadow' : 'text-muted-foreground'}`}>
            <Inbox className="w-4 h-4" />{t('expenses:approvals.queue', { defaultValue: 'Queue' })}
          </button>
          <button type="button" onClick={() => setTab('advances')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium ${tab === 'advances' ? 'bg-background text-foreground shadow' : 'text-muted-foreground'}`}>
            {t('expenses:tabs.advances', { defaultValue: 'Advances' })}
          </button>
          <button type="button" onClick={() => setTab('setup')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${tab === 'setup' ? 'bg-background text-foreground shadow' : 'text-muted-foreground'}`}>
            <Settings2 className="w-4 h-4" />{t('expenses:approvals.setup', { defaultValue: 'Setup' })}
          </button>
        </div>
      )}

      {tab === 'advances' ? (
        <div className="space-y-4">
        {/* Monthly retirement cycle and the reimbursement clock. Neither sends
            anything: a snapshot freezes balances, publishing records notices. */}
        <ImprestRetirementQueue />
        <ReimbursementSLAPanel />
        <AdvancesPanel finance onAction={(a: ExpenseAdvance) => (
          <span className="inline-flex gap-1">
            {a.status === 'REQUESTED' && (
              <Button size="sm" variant="outline" onClick={() => advanceAction(a.id, 'approve').then(() => window.location.reload()).catch((e) => toast.error(getApiErrorMessage(e, 'Could not approve.')))}>
                {t('expenses:buttons.approve', { defaultValue: 'Approve' })}
              </Button>
            )}
            {a.status === 'APPROVED' && (
              <Button size="sm" variant="outline" onClick={() => advanceAction(a.id, 'issue').then(() => window.location.reload()).catch((e) => toast.error(getApiErrorMessage(e, 'Could not issue.')))}>
                {t('expenses:buttons.issue', { defaultValue: 'Issue' })}
              </Button>
            )}
            {(a.status === 'PARTIALLY_RECONCILED') && (
              <Button size="sm" variant="outline" onClick={() => advanceAction(a.id, 'reconcile').then(() => window.location.reload()).catch((e) => toast.error(getApiErrorMessage(e, 'Could not reconcile.')))}>
                {t('expenses:buttons.reconcile', { defaultValue: 'Mark reconciled' })}
              </Button>
            )}
            {a.status === 'RECONCILED' && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => advanceAction(a.id, 'close').then(() => window.location.reload()).catch((e) => toast.error(getApiErrorMessage(e, 'Balance not fully reconciled yet.')))}>
                {t('expenses:buttons.close', { defaultValue: 'Close' })}
              </Button>
            )}
          </span>
        )} />
        </div>
      ) : tab === 'setup' ? <SetupTab /> : (
        <Card>
          <CardContent className="pt-4">
            {loading ? (
              <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
            ) : reports.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">
                <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
                {t('expenses:empty.approvals', { defaultValue: 'Nothing waiting for your review.' })}
                <div className="text-xs mt-2">
                  {t('expenses:empty.approvals_own_hint', { defaultValue: 'Reports you submitted yourself never appear here — someone else reviews those.' })}
                </div>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {reports.map((r) => (
                  <li key={r.id}>
                    <button type="button" className="w-full py-3 flex items-center gap-3 text-left hover:bg-muted/40 rounded-lg px-2"
                      onClick={() => setSelected(r.id)}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{r.title}</p>
                        <p className="text-xs text-muted-foreground">{r.reportNumber}{r.submittedAt ? ` · ${formatDate(r.submittedAt)}` : ''}</p>
                      </div>
                      <span className="text-sm font-semibold tabular-nums">{r.baseTotal} {r.baseCurrency}</span>
                      <StatusChip status={r.status} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExpenseApprovalsPage;
